import type { SiteId } from '@/lib/site'

// ScamDefense renders US English as its base copy. The GB/CA/AU keys are kept
// in the `MarketVariants<T>` shape so forked slide copy files (which still
// contain other-market overrides) compile unchanged; only US is rendered on
// the US faces.
//
// Two more keys serve the Qatar face (2026-09):
//   - `QA`: English overrides for a Gulf audience (riyals, Qatari agencies,
//     local examples). Partial: only the fields that need localizing.
//   - `ar`: the full Arabic translation of the QA-localized copy. Typed as
//     the complete `T` so a missed field is a compile error, not a silent
//     English fallback in the middle of an Arabic slide.
export const SUPPORTED_MARKETS = ['US', 'GB', 'CA', 'AU', 'QA'] as const

export type Market = (typeof SUPPORTED_MARKETS)[number]

export const CANONICAL_MARKET = 'US' as const
export type CanonicalMarket = typeof CANONICAL_MARKET
export type NonCanonicalMarket = Exclude<Market, CanonicalMarket>

export type MarketVariants<T> = { [CANONICAL_MARKET]: T } & {
  [K in NonCanonicalMarket]?: Partial<T>
} & {
  /** Arabic. Complete translation of the Qatar (QA-over-US) copy. */
  ar?: T
}

// Per-tenant copy overrides, layered on top of the US base copy at render
// time (see useSlideCopy). Lets a tenant like DCUC tailor specific fields
// (e.g. a military-flavored scam example) without forking the whole slide.
export type SiteVariants<T> = Partial<Record<SiteId, Partial<T>>>
